function cartEvents() {

  const {
    freeGwpProductIdTier1,
    freeGwpProductTier1InStock,
    freeGwpThresholdTier1,
    freeGwpThresholdTier2,
    freeGwpProductIdTier2,
    freeGwpProductTier2InStock,
    gwpSectionThreshold,
    gwpMaxItems,
  } = ACDC.Data;

  // ===========================================================================
  // PROMO WINDOW
  // Start: 4 Sep 2026 11:59pm ET   End: 7 Sep 2026 11:59:59pm ET   (EDT = UTC-4)
  //
  // This window MUST match the automatic discount's window in Shopify Admin.
  // If the JS opens before the discount does, the customer receives the "gift"
  // and pays full price for it.
  // The end uses :59 seconds because the comparison is `< promoEndTime`; if the
  // Shopify discount ends at midnight, change to '2026-09-08T00:00:00-04:00'.
  // ===========================================================================
  const promoStartTime = new Date('2026-09-02T03:00:00-04:00').getTime(); // 1788332400000
  const promoEndTime   = new Date('2026-09-09T03:00:00-04:00').getTime(); // 1788937200000

  // --- Behaviour flags -------------------------------------------------------
  // Hides the GWP section/carousel outside the promo window.
  // Set to false if that section is a permanent mechanic rather than part of
  // this promo.
  const GATE_GWP_SECTION_BY_PROMO = true;

  // Cleanup for legacy Tier 1 items (added by the previous version of this
  // script, without the _gwp_tier property). WARNING: with this set to true,
  // outside the promo ANY line for that variant gets removed — including one the
  // customer added and intends to pay for. Turn it on for a few days after the
  // promo closes to drain old carts, then turn it back off. Shopify carts expire
  // on their own after ~14 days.
  const REMOVE_LEGACY_UNMARKED_TIER1 = false;

  // --- Clock -----------------------------------------------------------------
  // Date.now() is the client's clock and can be wrong. We correct it using the
  // `Date` header from the first /cart.js response. This is not tamper-proof
  // (nothing client-side is), but it eliminates the accidental case.
  let serverTimeOffset = 0;
  const now = () => Date.now() + serverTimeOffset;

  const isPromoActive = () => {
    const t = now();
    return t >= promoStartTime && t < promoEndTime;
  };

  // Exposed for ajaxAddToCart(), which lives outside this scope.
  window.isGwpPromoActive = isPromoActive;

  let gwpAbortController = null;
  let cleanupRunning = false;

  function abortStaleGwpOps() {
    if (gwpAbortController) gwpAbortController.abort();
    gwpAbortController = new AbortController();
    return gwpAbortController.signal;
  }

  // ---------------------------------------------------------------------------
  // Gift line identification
  //
  // Tier 1 is tagged with _gwp_tier:"1" and the GWP section with _gwp:"true".
  // Deliberately different markers: if they shared one, the section cleanup block
  // would also remove the Tier 1 gift whenever the cart drops below the section
  // threshold.
  //
  // Detection goes by property (not variant id) so a line the customer added on
  // their own doesn't count as a gift — and removal goes by `key` (not id)
  // because /cart/update.js keyed on a variant id affects EVERY line for that
  // variant.
  // ---------------------------------------------------------------------------
  function isTier1Config() {
    return freeGwpProductIdTier1 != "" && freeGwpThresholdTier1 != "";
  }

  function findTier1Line(cartItems) {
    if (!isTier1Config()) return null;
    const tier1Id = Number(freeGwpProductIdTier1);
    return cartItems.find(function (i) {
      return i.id === tier1Id && i.properties && i.properties._gwp_tier === "1";
    }) || null;
  }

  function findLegacyTier1Line(cartItems) {
    if (!REMOVE_LEGACY_UNMARKED_TIER1) return null;
    if (!isTier1Config()) return null;
    const tier1Id = Number(freeGwpProductIdTier1);
    return cartItems.find(function (i) {
      return i.id === tier1Id && !(i.properties && i.properties._gwp_tier === "1");
    }) || null;
  }

  function sectionGwpLines(cartItems) {
    return cartItems.filter(function (i) {
      return i.properties && i.properties._gwp === "true";
    });
  }

  init();

  function init() {
    addEvents();
  }

  function gwpNeedsUpdate(totalCartPrice, cartItems) {
    const price = Number(totalCartPrice);
    const itemIds = cartItems.map(function (item) { return item.id; });

    // ---- Tier 1 (promo-gated) ----
    if (isTier1Config()) {
      const tier1Line = findTier1Line(cartItems);
      if (!isPromoActive()) {
        if (tier1Line || findLegacyTier1Line(cartItems)) return true;
      } else {
        const aboveTier1 = price >= Number(freeGwpThresholdTier1);
        if (aboveTier1 && !tier1Line && freeGwpProductTier1InStock) return true;
        if (!aboveTier1 && tier1Line) return true;
      }
    }

    // ---- Tier 2 (NOT promo-gated) ----
    // The config is empty today, so this block never runs. If it ever gets filled
    // in and the gift is part of a dated promo, wrap it in isPromoActive() the
    // same way Tier 1 is, and tag/detect it with _gwp_tier:"2".
    if (freeGwpProductIdTier2 != "" && freeGwpThresholdTier2 != "") {
      const hasTier2 = itemIds.includes(Number(freeGwpProductIdTier2));
      const aboveTier2 = price >= Number(freeGwpThresholdTier2);
      if (aboveTier2 && !hasTier2 && freeGwpProductTier2InStock) return true;
      if (!aboveTier2 && hasTier2) return true;
    }

    // ---- GWP section ----
    const sectionLines = sectionGwpLines(cartItems);
    if (sectionLines.length > 0) {
      if (gwpSectionThreshold && gwpSectionThreshold != "" && price < Number(gwpSectionThreshold)) return true;
      if (GATE_GWP_SECTION_BY_PROMO && !isPromoActive()) return true;
    }

    return false;
  }

  // Batched GWP sync — computes all adds/removes from cart data, executes in max 2 API calls
  async function processGwpSync(cartData, signal) {
    const totalCartPrice = Number(cartData.total_price);
    const cartItems = cartData.items;
    const itemIds = cartItems.map(function (item) { return item.id; });

    const itemsToAdd = [];
    const updates = {};

    // ---- Tier 1 (promo-gated) ----
    if (isTier1Config()) {
      const tier1Id = Number(freeGwpProductIdTier1);
      const tier1Line = findTier1Line(cartItems);

      if (!isPromoActive()) {
        if (tier1Line) updates[tier1Line.key] = 0;
        const legacy = findLegacyTier1Line(cartItems);
        if (legacy) updates[legacy.key] = 0;
      } else if (
        totalCartPrice >= Number(freeGwpThresholdTier1) &&
        !tier1Line &&
        freeGwpProductTier1InStock
      ) {
        itemsToAdd.push({
          quantity: 1,
          id: tier1Id,
          properties: { _gwp_tier: "1" },
        });
      } else if (totalCartPrice < Number(freeGwpThresholdTier1) && tier1Line) {
        updates[tier1Line.key] = 0;
      }
    }

    // ---- Tier 2 (NOT promo-gated — see note in gwpNeedsUpdate) ----
    if (freeGwpProductIdTier2 != "" && freeGwpThresholdTier2 != "") {
      const tier2Id = Number(freeGwpProductIdTier2);
      const hasTier2 = itemIds.includes(tier2Id);
      if (totalCartPrice >= Number(freeGwpThresholdTier2) && !hasTier2 && freeGwpProductTier2InStock) {
        itemsToAdd.push({ quantity: 1, id: tier2Id });
      } else if (totalCartPrice < Number(freeGwpThresholdTier2) && hasTier2) {
        const tier2Line = cartItems.find(function (i) { return i.id === tier2Id; });
        if (tier2Line) updates[tier2Line.key] = 0;
      }
    }

    // ---- GWP section: remove if below threshold or if the promo isn't active ----
    const belowSectionThreshold =
      gwpSectionThreshold && gwpSectionThreshold != "" && totalCartPrice < Number(gwpSectionThreshold);
    const sectionOutOfWindow = GATE_GWP_SECTION_BY_PROMO && !isPromoActive();

    if (belowSectionThreshold || sectionOutOfWindow) {
      sectionGwpLines(cartItems).forEach(function (item) {
        updates[item.key] = 0;
      });
    }

    let cartChanged = false;

    if (itemsToAdd.length > 0) {
      if (signal.aborted) return;
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: itemsToAdd }),
        signal: signal,
      });
      cartChanged = true;
    }

    if (Object.keys(updates).length > 0) {
      if (signal.aborted) return;
      await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ updates: updates }),
        signal: signal,
      });
      cartChanged = true;
    }

    if (cartChanged) {
      if (window.themeVariables.settings.pageType === "cart") {
        await refreshCartPage();
      } else {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }
    }
  }

  // Guarded cleanup for stale GWP section items — batch removal in single API call
  async function cleanupStaleGwpItems(cartDataOverride) {
    if (!gwpSectionThreshold || gwpSectionThreshold === '') return;
    if (cleanupRunning) return;
    cleanupRunning = true;

    try {
      const cart = cartDataOverride || await (await fetch('/cart.js')).json();

      const belowThreshold = Number(cart.total_price) < Number(gwpSectionThreshold);
      const outOfWindow = GATE_GWP_SECTION_BY_PROMO && !isPromoActive();
      if (!belowThreshold && !outOfWindow) return;

      const updates = {};
      sectionGwpLines(cart.items).forEach(function (item) {
        updates[item.key] = 0;
      });

      if (Object.keys(updates).length === 0) return;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ updates: updates }),
      });

      if (window.themeVariables.settings.pageType === 'cart') {
        await refreshCartPage();
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } finally {
      cleanupRunning = false;
    }
  }

  // Section Rendering API refresh for cart page — no full page reload
  async function refreshCartPage() {
    const cartSection = document.querySelector('.shopify-section[id*="main-cart"]');
    if (!cartSection) {
      window.location.reload();
      return;
    }
    const sectionId = cartSection.id.replace('shopify-section-', '');

    try {
      const response = await fetch(Shopify.routes.root + '?section_id=' + sectionId);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newSection = doc.getElementById(cartSection.id);
      if (newSection) {
        cartSection.innerHTML = newSection.innerHTML;
      } else {
        window.location.reload();
      }
    } catch (e) {
      window.location.reload();
    }
  }

  // Initial cart state check
  fetch("/cart.js")
    .then(function (resp) {
      // Calibrate the clock against the server before evaluating the promo window.
      const srvDate = resp.headers.get('Date');
      if (srvDate) {
        const parsed = new Date(srvDate).getTime();
        if (!isNaN(parsed)) serverTimeOffset = parsed - Date.now();
      }
      return resp.json();
    })
    .then(function (json) {
      const totalCartPrice = json.total_price;
      const cartItems = json.items;
      toggleGwpSection(totalCartPrice);
      handleGWPCartActions(cartItems);

      const needsUpdate = gwpNeedsUpdate(totalCartPrice, cartItems);

      // Outside the window, cleanup runs on ANY page, not just /cart. Otherwise a
      // cart holding the gift shows it as free in the drawer until something
      // fires cart:change, and then checkout charges full price for it.
      if (!isPromoActive()) {
        if (needsUpdate) {
          const signal = abortStaleGwpOps();
          processGwpSync(json, signal).catch(function (err) {
            if (err.name !== 'AbortError') console.error('[GWP]', err);
          });
        }
        return;
      }

      if (window.themeVariables.settings.pageType === "cart") {
        if (needsUpdate) {
          const signal = abortStaleGwpOps();
          processGwpSync(json, signal).catch(function (err) {
            if (err.name !== 'AbortError') console.error('[GWP]', err);
          });
        } else {
          cleanupStaleGwpItems(json);
        }
      }
    });

  function addEvents() {
    // cart:refresh — toggle visibility + sync buttons (debounced).
    // On the cart page the line-item table/totals are server-rendered, so a GWP
    // add (ajaxAddToCart) won't show until the section is re-rendered. Reuse
    // refreshCartPage() here, then rebind the carousel arrows and re-sync state.
    let refreshDebounceTimer = null;
    document.addEventListener('cart:refresh', function () {
      clearTimeout(refreshDebounceTimer);
      refreshDebounceTimer = setTimeout(async function () {
        if (window.themeVariables.settings.pageType === "cart") {
          await refreshCartPage();
          initGwpCarouselArrows();
        }
        fetch("/cart.js")
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            toggleGwpSection(cart.total_price);
            handleGWPCartActions(cart.items);
          });
      }, 300);
    });

    // cart:change — use event.detail.cart directly, no extra /cart.js fetch
    document.addEventListener('cart:change', function (event) {
      const signal = abortStaleGwpOps();
      const cartData = event.detail && event.detail.cart;

      if (cartData) {
        toggleGwpSection(cartData.total_price);
        processGwpSync(cartData, signal).catch(function (err) {
          if (err.name !== 'AbortError') console.error('[GWP]', err);
        });
      } else {
        fetch("/cart.js", { signal: signal })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            toggleGwpSection(cart.total_price);
            processGwpSync(cart, signal);
          })
          .catch(function (err) {
            if (err.name !== 'AbortError') console.error('[GWP]', err);
          });
      }
    });
  }

  function toggleGwpSection(totalCartPrice) {
    if (gwpSectionThreshold) {
      // Multiple instances can coexist (cart drawer + cart page), so toggle each.
      const promoOk = !GATE_GWP_SECTION_BY_PROMO || isPromoActive();
      const gwpSections = document.querySelectorAll(".minicart-gwp-wrap");
      gwpSections.forEach(function (gwpSection) {
        gwpSection.style.removeProperty('display');
        if (
          promoOk &&
          gwpSectionThreshold != "" &&
          Number(totalCartPrice) >= Number(gwpSectionThreshold)
        ) {
          gwpSection.classList.add("gwp-visible");
        } else {
          gwpSection.classList.remove("gwp-visible");
        }
      });
    }
  }

  // Override prev/next button state for GWP carousel.
  // Re-callable so the cart page can rebind after refreshCartPage() replaces the DOM.
  function initGwpCarouselArrows() {
    // Multiple instances can coexist (cart drawer + cart page); bind each one.
    const gwpSections = document.querySelectorAll('.minicart-gwp-wrap');
    gwpSections.forEach(function (gwpSection) {
      const carousel = gwpSection.querySelector('scroll-carousel');
      const prevBtn = gwpSection.querySelector('carousel-prev-button button');
      const nextBtn = gwpSection.querySelector('carousel-next-button button');
      if (!carousel || !prevBtn || !nextBtn) return;

      // Avoid duplicate listeners when rebound after a DOM replacement.
      if (carousel.dataset.arrowsBound === 'true') return;
      carousel.dataset.arrowsBound = 'true';

      carousel.addEventListener('scroll', function () {
        const scrollLeft = Math.round(Math.abs(carousel.scrollLeft));
        const maxScroll = carousel.scrollWidth - carousel.clientWidth;

        prevBtn.disabled = scrollLeft <= 0;
        nextBtn.disabled = scrollLeft >= maxScroll - 1;
      });
    });
  }
  initGwpCarouselArrows();

  function handleGWPCartActions(cartItems) {
    let gwpItemCount = 0;
    const maxGwpItems = gwpMaxItems ? parseInt(gwpMaxItems) : 1;
    const gwpForms = document.querySelectorAll('.minicart-gwp-form');

    // Outside the window, no section button should be able to add anything.
    if (GATE_GWP_SECTION_BY_PROMO && !isPromoActive()) {
      gwpForms.forEach(function (form) {
        form.querySelector('.add-to-cart').setAttribute('disabled', true);
        form.querySelector('.atb-ajax-plp').innerText = 'Add';
      });
      return;
    }

    gwpForms.forEach(function (form) {
      const variantId = form.querySelector('input[name="id"]').value;
      const isInCart = cartItems.some(function (item) {
        return item.id == parseInt(variantId) && item.properties && item.properties._gwp === "true";
      });
      if (isInCart) {
        gwpItemCount++;
        form.querySelector('.atb-ajax-plp').innerText = 'Added';
        form.querySelector('.add-to-cart').setAttribute('disabled', true);
      }
    });

    if (gwpItemCount >= maxGwpItems) {
      gwpForms.forEach(function (form) {
        form.querySelector('.add-to-cart').setAttribute('disabled', true);
      });
    } else {
      gwpForms.forEach(function (form) {
        const variantId = form.querySelector('input[name="id"]').value;
        const isInCart = cartItems.some(function (item) {
          return item.id == parseInt(variantId) && item.properties && item.properties._gwp === "true";
        });
        if (!isInCart) {
          form.querySelector('.add-to-cart').removeAttribute('disabled');
          form.querySelector('.atb-ajax-plp').innerText = 'Add';
        }
      });
    }
  }

  window.syncGwpButtonStates = handleGWPCartActions;
}

window.addEventListener("DOMContentLoaded", function () {
  cartEvents();
});


function ajaxAddToCart(event) {
  event.preventDefault();

  // Defensive guard in case the button stays reachable outside the promo window.
  if (window.isGwpPromoActive && !window.isGwpPromoActive()) return;

  const id = event.target.querySelector('input[name="id"]').value;

  const allGwpButtons = document.querySelectorAll('.minicart-gwp-form .add-to-cart');
  allGwpButtons.forEach(function (btn) { btn.setAttribute('disabled', true); });

  event.target.querySelector('.atb-ajax-plp').innerText = 'Loading...';

  fetch("/cart.js")
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      const alreadyGwp = cart.items.some(function (item) {
        return item.id == parseInt(id) && item.properties && item.properties._gwp === "true";
      });
      if (alreadyGwp) {
        event.target.querySelector('.atb-ajax-plp').innerText = 'Added';
        return;
      }
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ quantity: 1, id: parseInt(id), properties: { "_gwp": "true" } }]
        }),
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        });
    });
}